import { useState } from "react";
import { Button } from "@/components/ui/button";
import { QueryKeys } from "@/constants";
import IDENTIFIERS from "@/constants/test_id";
import { queryClient } from "@/hooks/use-query-client";
import type { Relation } from "@/model/demographics";
import type Widget from "@/model/widget";
import PersonAvatar from "@/components/custom-ui/person-avatar";
import { InviteUser } from "@/pages/invite-user/invite-user";
import { LogCategories } from "@/services/logger/logger-types";
import WidgetCard from "./widget-card";

export function InviteUserWidget({ widget }: { widget: Widget }) {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");

  const dependents =
    (queryClient.getQueryData([QueryKeys.DEPENDENTS]) as Relation[]) ?? [];

  function handleInviteDependent(memberId: string) {
    setSelectedMemberId(memberId);
    setInviteDialogOpen(true);
  }

  return (
    <>
      <WidgetCard title={widget?.widgetDisplayName}>
        <div className="flex h-full flex-col gap-2">
          <p className="text-[0.875rem]">
            Invite members in your plan so that they can also benefit from all
            the plan and benefit information.
          </p>
          <div className="flex max-h-60 flex-1 flex-col gap-2 overflow-y-auto">
            {dependents.length === 0 && (
              <p className="text-[0.875rem] text-neutral-500">
                No family members found.
              </p>
            )}
            {dependents.map(
              (dependent, index) =>
                !dependent.username && (
                  <div
                    key={dependent.memberId}
                    className={`flex items-center justify-between gap-2 py-2 text-[0.875rem] font-medium ${index === 0 ? "" : "border-t"}`}
                  >
                    <span className="flex items-center gap-2">
                      <PersonAvatar
                        className="size-10"
                        firstName={dependent.firstName}
                        lastName={dependent.lastName}
                      />
                      {dependent.firstName} {dependent.lastName}
                    </span>
                    <Button
                      className="min-w-24"
                      logCategory={LogCategories.INVITE_USER_WIDGET}
                      data-testid={IDENTIFIERS.INVITE_USER.WIDGET_INVITE_BUTTON}
                      onClick={() => handleInviteDependent(dependent.memberId)}
                    >
                      Invite
                    </Button>
                  </div>
                ),
            )}
          </div>
        </div>
      </WidgetCard>

      <InviteUser
        dialogOpen={inviteDialogOpen}
        setDialogOpen={setInviteDialogOpen}
        preselectedMemberId={selectedMemberId}
        logDialogName="Invite User"
        logTrigger="Invite User Widget"
      />
    </>
  );
}
